import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";

import {
  requestPasswordReset,
  requestSupervisorEmailCode,
  signInWithSupabase,
  signOutSupervisor,
  updateRecoveredPassword,
  verifySupervisorEmailCode,
  type Credentials,
  type SafeMessageResult,
  type SignInResult,
} from "./auth";

export interface AuthUser {
  id: string;
  email: string | null;
}

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  recoveryMode: boolean;
  signIn: (credentials: Credentials) => Promise<SignInResult>;
  requestEmailCode: (email: string) => Promise<SafeMessageResult>;
  verifyEmailCode: (input: { email: string; code: string }) => Promise<SignInResult>;
  requestPasswordReset: (email: string) => Promise<SafeMessageResult>;
  updatePassword: (password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Wraps the app in supabase-js-backed auth. On mount it restores any persisted session
 * (stay-logged-in: supabase-js rehydrates from storage + silently refreshes), then tracks changes
 * via onAuthStateChange. The supabase client is INJECTED so this is testable with a fake.
 */
export function AuthProvider({
  client,
  children,
}: {
  client: SupabaseClient;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [recoveryMode, setRecoveryMode] = useState(false);

  useEffect(() => {
    let active = true;

    function applySession(session: Session | null, event?: string): void {
      if (!active) return;
      if (event === "PASSWORD_RECOVERY") {
        setRecoveryMode(true);
      }
      if (session && session.user) {
        setUser({ id: session.user.id, email: session.user.email ?? null });
        setStatus("authenticated");
      } else {
        setUser(null);
        setStatus("unauthenticated");
      }
    }

    client.auth
      .getSession()
      .then(({ data }) => applySession(data.session))
      .catch(() => applySession(null));

    const { data } = client.auth.onAuthStateChange((event, session) => applySession(session, event));

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      recoveryMode,
      signIn: (credentials: Credentials) => signInWithSupabase(client, credentials),
      requestEmailCode: (email: string) => requestSupervisorEmailCode(client, email, window.location.origin),
      verifyEmailCode: (input: { email: string; code: string }) => verifySupervisorEmailCode(client, input),
      requestPasswordReset: (email: string) => requestPasswordReset(client, email, window.location.origin),
      updatePassword: async (password: string) => {
        const result = await updateRecoveredPassword(client, password);
        if (result.ok) {
          setRecoveryMode(false);
        }
        return result;
      },
      signOut: () => signOutSupervisor(client),
    }),
    [client, recoveryMode, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
